
import React, { useState } from "react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { RotateCcw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/hooks/use-mobile";

interface SessionsModalActionsProps {
  onCancel: () => void;
  onSave: () => void;
  onReset: () => void;
  isPending: boolean;
  hasActiveSubscriptions: boolean;
}

const SessionsModalActions: React.FC<SessionsModalActionsProps> = ({
  onCancel,
  onSave,
  onReset,
  isPending,
  hasActiveSubscriptions
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [showResetDialog, setShowResetDialog] = useState(false);

  const handleResetClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowResetDialog(true);
  };

  const handleResetConfirm = () => {
    onReset();
    setShowResetDialog(false);
  };

  const handleResetCancel = () => {
    setShowResetDialog(false);
  };

  const resetButton = (
    <Button 
      variant="outline" 
      className="flex-1 h-11 text-sm flex items-center gap-2" 
      disabled={isPending} 
      onClick={handleResetClick}
      type="button"
    >
      <RotateCcw className="h-4 w-4" />
      {t('clients.resetToZero')}
    </Button>
  );

  return (
    <div className="flex flex-col gap-3 w-full">
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
        <Button variant="outline" onClick={onCancel} className="flex-1 h-11 text-sm" disabled={isPending}>
          {t('common.cancel')}
        </Button>
        
        {hasActiveSubscriptions && resetButton}
        
        <Button onClick={onSave} disabled={isPending || !hasActiveSubscriptions} className="flex-1 h-11 text-sm font-medium">
          {isPending ? t('clients.saving') : t('clients.saveSessions')}
        </Button>
      </div>

      {/* Reset Confirmation Modal - Mobile */}
      {isMobile && (
        <Sheet open={showResetDialog} onOpenChange={setShowResetDialog}>
          <SheetContent side="bottom" className="h-auto rounded-t-xl border-0 p-6 modal-safe-area-aware">
            <SheetHeader className="text-left pb-4 px-[18px]">
              <SheetTitle className="greek-text">
                {t('clients.resetConfirmation')}
              </SheetTitle>
              <SheetDescription className="greek-text">
                {t('clients.confirmReset')}
              </SheetDescription>
            </SheetHeader>
            <SheetFooter className="flex flex-col gap-3 pt-4">
              <Button onClick={handleResetConfirm} className="w-full h-11">
                {t('clients.resetSessions')}
              </Button>
              <Button variant="outline" onClick={handleResetCancel} className="w-full h-11">
                {t('common.cancel')}
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      )}

      {/* Reset Confirmation Modal - Desktop */}
      {!isMobile && (
        <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader className="text-left">
              <DialogTitle className="greek-text">
                {t('clients.resetConfirmation')}
              </DialogTitle>
              <DialogDescription className="greek-text">
                {t('clients.confirmReset')}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex flex-col gap-2 sm:flex-row">
              <Button variant="outline" onClick={handleResetCancel}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleResetConfirm}>
                {t('clients.resetSessions')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default SessionsModalActions;
